import React, { PropsWithChildren, useContext, useState } from 'react'
import ConfirmNavigationAwayModal from '../Modals/ConfirmNavigationAwayModal'

export interface NavigationInformation {
  pathname?: string
  state?: {
    communityKey?: number
    communityName?: string
    academicYear?: string
    [k: string]: unknown
  }
  search?: string
  tab?: string
  callback?: () => Promise<void>
}

/**
 * NavigationAway Context default value including
 *
 * isConfirmationModalOpen: Boolean value controlling the display of the confirmation modal
 * setConfirmationModalState: Update the state of the confirmation modal to show/hide it
 * actionConfirmed: Boolean value denoting if the navigation away (and consequences) were accepted or denied.
 *    This boolean allows the calling component to continue on or revert the navigation.
 *
 * updateActionConfirmed: Update the user's confirmation of the navigation action.
 *    To be utilized by ConfirmNavigationAwayModal to show confirmation/denial.
 *    Because this is the `actionConfirmed` setter and the calling component resolves
 *    based on `actionConfirmed` this will need to be called after resolution.
 *
 * navigationInformation: The information surrounding navigation. We allow pretty much anything
 *    but will specifically look for pathname given that's how we'll navigate away.
 *
 * updateNavigationInformation: Update the navigationInformation with new pathname, state, or
 *    whatever else wants to be stored.
 *
 * initiateBlock: The boolean to initiate a navigation block. False bypasses the block and true
 * begins the block.
 *
 * updateInitiateBlock: Update the initiateBlock property as needed
 *
 * isEditMode: Boolean to determine what state the page is in. Since we generally block on edits,
 * we consider a form to be filled to have the page in an editable state. Changes should be confirmed
 * to discard before proceeding.
 *
 * updateEditMode: Update the isEditMode boolean based on the state of the page/form.
 *
 * navigationCallback: called after each navigation handler is called to ensure any action needing
 * to occur after the block is accepted occurs. An example of this is the Logout button. The
 * navigationInformation contains an optional callback property that will be called here.
 *
 * validateActionConfirmed: A method to be called at the beginning of each navigationHandler
 * to reset the block and validate an action was confirmed. If an action was not confirmed,
 * it is imperative to return from the navigationHandler
 */
const defaultNavigationAwayContextValue = {
  isConfirmationModalOpen: false,
  setConfirmationModalState: (state: boolean) => {
    console.warn(
      `The default value of NavigationAwayContext.setConfirmationModalState was called with state=${state}. Did you forget to install a NavigationAwayProvider?`
    )
  },
  actionConfirmed: false as boolean | undefined,
  updateActionConfirmed: (state: boolean | undefined) => {
    console.warn(
      `The default value of NavigationAwayContext.setConfirmationModalState was called with state=${state}. Did you forget to install a NavigationAwayProvider?`
    )
  },
  navigationInformation: {} as NavigationInformation,
  updateNavigationInformation: (newInfo: NavigationInformation) => {
    console.warn(
      `The default value of NavigationAwayContext.updateNavigationInformation was called with state=${newInfo}. Did you forget to install a NavigationAwayProvider?`
    )
  },
  initiateBlock: false,
  updateInitiateBlock: (updatedBlock: boolean) => {
    console.warn(
      `The default value of NavigationAwayContext.updateInitiateBlock was called with state=${updatedBlock}. Did you forget to install a NavigationAwayProvider?`
    )
  },
  isEditMode: false,
  updateEditMode: (updatedEditMode: boolean) => {
    console.warn(
      `The default value of NavigationAwayContext.updateEditMode was called with state=${updatedEditMode}. Did you forget to install a NavigationAwayProvider?`
    )
  },
  navigationCallback: () => {
    console.warn(
      `The default value of NavigationAwayContext.navigationCallback was called. Did you forget to install a NavigationAwayProvider?`
    )
  },
  validateActionConfirmed: (): boolean => {
    console.warn(
      `The default value of NavigationAwayContext.validateActionConfirmed was called. Did you forget to install a NavigationAwayProvider?`
    )
    return false
  },
}

/**
 * Context built by the default values. If the useNavigationAwayContext is called
 * without a provider wrapping it the default values will be used and warnings
 * will show for unimplemented methods.
 */
const NavigationAwayContext = React.createContext(
  defaultNavigationAwayContextValue
)
export const useNavigationAwayContext =
  (): typeof defaultNavigationAwayContextValue => {
    return useContext(NavigationAwayContext)
  }

/**
 * Provider for the NavigationAwayContext
 *
 * Implements and provides an implemented context value for use within the provider.
 *
 * @param param0 children
 * @returns NavigationAwayContext.Provider JSX wrapping the children
 */
export const NavigationAwayProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const setConfirmationModalState = (state: boolean) => {
    setIsConfirmationModalOpen(state)
  }

  /**
   * We leave undefined as part of the type so we can clarify if we've Accepted, Denied, or no action has been set.
   */
  const [actionConfirmed, setActionConfirmed] = useState<boolean | undefined>(
    undefined
  )

  const updateActionConfirmed = (state: boolean | undefined) => {
    setActionConfirmed(state)
    // Close the modal after confirmation.
    setConfirmationModalState(false)
  }

  const [navigationInformation, setNavigationInformation] =
    useState<NavigationInformation>({
      pathname: '',
    })

  const updateNavigationInformation = (newInfo: NavigationInformation) => {
    setNavigationInformation(newInfo)
  }

  const [initiateBlock, setInitiateBlock] = useState(false)

  const updateInitiateBlock = (updatedBlock: boolean) => {
    setInitiateBlock(updatedBlock)
  }

  const [isEditMode, setIsEditMode] = useState(false)

  const updateEditMode = (updatedEditMode: boolean) => {
    setIsEditMode(updatedEditMode)
  }

  const navigationCallback = () => {
    navigationInformation.callback && navigationInformation.callback()
  }

  const validateActionConfirmed = (): boolean => {
    updateInitiateBlock(false)
    if (!actionConfirmed) {
      updateActionConfirmed(undefined)
      return false
    }
    return true
  }

  const value = {
    isConfirmationModalOpen,
    setConfirmationModalState,
    actionConfirmed,
    updateActionConfirmed,
    navigationInformation,
    updateNavigationInformation,
    initiateBlock,
    updateInitiateBlock,
    isEditMode,
    updateEditMode,
    navigationCallback,
    validateActionConfirmed,
  }

  return (
    <NavigationAwayContext.Provider value={value}>
      <ConfirmNavigationAwayModal
        isOpen={isConfirmationModalOpen}
        handleFormSubmit={async (event: React.FormEvent<HTMLDivElement>) => {
          event.preventDefault()
          updateActionConfirmed(true)
        }}
        handleConfirmationCancel={() => updateActionConfirmed(false)}
      />
      {children}
    </NavigationAwayContext.Provider>
  )
}

export default NavigationAwayProvider
